// Empire Apps Chassis · identity-zkp · commitment scheme
// Hash-based hiding/binding commitments using HMAC-SHA256 with a random
// blinding factor. This is what the privacy literature calls a "Pedersen-like
// hash commitment". It gives:
//   - Hiding: high-entropy blinding factor makes the commitment leak nothing
//   - Binding: SHA-256 collision resistance prevents the prover from opening
//     the same commitment to two different values
//
// This is the building block for our selective-disclosure layer. A full Pedersen
// commitment on BLS12-381 (homomorphic) is deferred to v1.1; the hash commitment
// here is what BBS+ uses under the hood for non-revealed attributes anyway.

import type { BlindingFactor, Commitment, CommitmentRecord, AttributeValue } from './types.ts';

const enc = new TextEncoder();

export function newBlindingFactor(): BlindingFactor {
  return crypto.getRandomValues(new Uint8Array(32)) as BlindingFactor;
}

export async function commit(
  attribute: string,
  value: AttributeValue,
  blinding: BlindingFactor,
): Promise<Commitment> {
  const k = await crypto.subtle.importKey(
    'raw',
    blinding as BufferSource,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const payload = enc.encode(`${attribute}${serialise(value)}`);
  const mac = new Uint8Array(await crypto.subtle.sign('HMAC', k, payload as BufferSource));
  return toHex(mac) as Commitment;
}

export async function verifyCommitment(
  attribute: string,
  value: AttributeValue,
  blinding: BlindingFactor,
  expected: Commitment,
): Promise<boolean> {
  const got = await commit(attribute, value, blinding);
  return constantTimeEqual(got, expected);
}

export async function commitMany(
  attributes: Record<string, AttributeValue>,
): Promise<Record<string, CommitmentRecord>> {
  const out: Record<string, CommitmentRecord> = {};
  const now = new Date().toISOString();
  for (const [attribute, value] of Object.entries(attributes)) {
    const blinding = newBlindingFactor();
    const commitment = await commit(attribute, value, blinding);
    out[attribute] = {
      attribute,
      commitment,
      blindingFactorHex: toHex(blinding),
      committedAt: now,
    };
  }
  return out;
}

export function serialise(value: AttributeValue): string {
  if (typeof value === 'string') return `s:${value}`;
  if (typeof value === 'number') return `n:${value}`;
  if (typeof value === 'boolean') return `b:${value ? '1' : '0'}`;
  throw new Error('commitment/serialise: unsupported value type');
}

export function toHex(b: Uint8Array): string {
  let s = '';
  for (const x of b) s += (x as number).toString(16).padStart(2, '0');
  return s;
}

export function fromHex(s: string): Uint8Array {
  const out = new Uint8Array(s.length >> 1);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(s.slice(i << 1, (i << 1) + 2), 16);
  return out;
}

function constantTimeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}
